import {useMemo} from 'react';
import {useTranslation} from 'react-i18next';
import {useSelector} from 'react-redux';
import {FormikHelpers} from 'formik';
import {TShow} from '../../../../hooks/useShow';
import {RootState} from '../../../../redux/store.type';
import {TSlot, TTimetableItem} from '../../../../redux/timetable/timetable.type';
import {createLecture, updateLecture} from '../../../../redux/timetable/timetableActions';
import {combineDateTime, getStartOfDay} from '../../../../library/dates';
import {useTimetableDays} from '../../hooks/useTimetableDays';
import {useEventTimeRange} from '../../hooks/useEventTimeRange';
import {getLectureTime} from '../../hooks/useTimeGroups';
import {initialValueLecture, buildValidationSchema, serializeLectureLink} from './constants';
import {LectureFormValues} from './types';

const findBreakAtTime = (
    dayLectures: TTimetableItem[],
    time: number,
    selfTtid?: number,
) => dayLectures.find(b =>
    b.isBreak
    && b.ttid !== selfTtid
    && b.time <= time
    && (b.timeEnd === undefined ? b.time === time : time < b.timeEnd),
);

export function useLectureForm(
    show: TShow,
    lecture: TTimetableItem | null,
    defaultTsid: number | null,
) {
    const {t} = useTranslation('timetable');
    const slots = useSelector((state: RootState) => state.timetable.slots);
    const lecturesByDay = useSelector((state: RootState) => state.timetable.lectures);
    const {isWithin} = useEventTimeRange();
    const {conferenceDays, currentDayIndex} = useTimetableDays();
    const isEdit = !!lecture;

    const initialDayIndex = lecture ? conferenceDays.findIndex(d => d.timestamp === getStartOfDay(getLectureTime(lecture, slots))) : currentDayIndex;
    const initialValues: LectureFormValues = {
        ...initialValueLecture(lecture),
        tsid: lecture?.tsid ?? (initialDayIndex === -1 ? 0 : defaultTsid),
        dayIndex: initialDayIndex,
    };

    const validationSchema = useMemo(
        () => buildValidationSchema(t('fieldRequired'), t('linkInvalidUrl'), t('linkTooLong', {max: 2048}), t('linkNameTooLong', {max: 50})),
        [t],
    );

    const handleSubmit = async (
        _changed: unknown,
        values: LectureFormValues,
        helpers: FormikHelpers<LectureFormValues>,
    ) => {
        const hasDate = !!values.tsid;
        let time = 0;

        if(hasDate) {
            const slot = slots.find((s: TSlot) => s.tsid === values.tsid);
            const day = conferenceDays[values.dayIndex];
            if(!slot || !day) {
                helpers.setSubmitting(false);
                return;
            }

            time = combineDateTime(day.timestamp, slot.timeStart);
            if(!isWithin(time)) {
                helpers.setFieldError('tsid', t('lectureOutsideEventHours'));
                helpers.setSubmitting(false);
                return;
            }
            const dayLectures = lecturesByDay[day.timestamp] ?? [];
            if(findBreakAtTime(dayLectures, time, lecture?.ttid)) {
                helpers.setFieldError('tsid', t('breakOverlapsTime'));
                helpers.setSubmitting(false);
                return;
            }
        }

        helpers.setSubmitting(true);
        try {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const {hasCasualPrice, title, link, linkName, priceCasual, amount, dayIndex: _dayIndex, ...rest} = values;
            const payload = {
                ...rest,
                title: (title ?? '').trim(),
                link: serializeLectureLink(linkName, link),
                priceCasual: hasCasualPrice ? priceCasual : null,
                amount,
                time,
            };
            const result = isEdit
                ? await updateLecture(lecture!.ttid, payload)
                : await createLecture(payload);
            if(result) show.close();
        } finally {
            helpers.setSubmitting(false);
        }
    };

    return {initialValues, validationSchema, handleSubmit, isEdit};
}
